/* globals showModalInfo */
import React, { useContext } from "react";
import { composeApplyCommand } from "./api";
import {
  parsePktEngParameters,
  parseStrictSystemSecurityIsActive,
  parseSubscriberIDSource,
  parseIPV6Prefix,
  parseUDRRate,
  parseInactivityTimeout,
} from "./api";
import GeneralSettings from "./Settings";
import help from "help/viewGeneralSettingsConfig.md";
import SettingsView from "common/SettingsView";
import SettingsList from "./SettingsList";
import BqnContext from "common/BqnContext";
import Loading from "common/Loading";
import { atBqnTarget } from "common/api";

const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const clear = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const joinSettings = (settings) =>
  settings.reduce((previous, current) => Object.assign(previous, current), {});

const requestAndParsePktEngParams = (mode, bqnId) =>
  ifCl
    .run(atBqnTarget("show pkteng parameters", mode, bqnId))
    .then(parsePktEngParameters);

const requestAndParseStrictSystemSecurityActive = (mode, bqnId) =>
  ifCl
    .run(atBqnTarget("show system security", mode, bqnId))
    .then(parseStrictSystemSecurityIsActive);

const requestAndParseUDRRate = (mode, bqnId) =>
  ifCl
    .run(atBqnTarget("show pkteng udr rate", mode, bqnId))
    .then(parseUDRRate);

// The source of the subscriber ID, the IPv6 prefix and the parameters of the
// web service are the same ones in the whole cluster: they are not asked of
// the BQN selected.
const requestAndParseSubcriberIDSource = () =>
  ifCl.run("show api subscriber parameters").then(parseSubscriberIDSource);

const requestAndParseIPV6Prefix = () =>
  ifCl.run("show subscriber ipv6-prefix").then(parseIPV6Prefix);

const requestAndParseInactivityTimeout = () =>
  ifCl.run("show service web parameters").then(parseInactivityTimeout);

const load = (mode, bqnId) =>
  Promise.all([
    requestAndParsePktEngParams(mode, bqnId),
    requestAndParseSubcriberIDSource(),
    requestAndParseInactivityTimeout(),
    requestAndParseStrictSystemSecurityActive(mode, bqnId),
    requestAndParseUDRRate(mode, bqnId),
    requestAndParseIPV6Prefix(),
  ]).then(joinSettings);

const mayWarnUserIfHostnameChanged = (previous, { hostname }) =>
  previous.hostname === hostname
    ? (response) => response
    : (response) => {
        if (response.length === 0 || response.includes("%WARN")) {
          showModalInfo(
            "The change in hostname will only be fully effective after a system reboot",
            null
          );
        }
      };

const ConfigGeneralSettings = ({returnView}) => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  const checkNothingPending = () =>
    ifCl.run("show config diff").then((response) => {
      if (response.length > 0) {
        throw new Error(CANT_PROCEED_ERROR);
      }
    });

  const submitCommand = (command) => ifCl.run(command, /*is batch*/ true);

  const clearConfigAttempt = () =>
    ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

  const notifyAndRevert = (error) => {
    error = error.message === undefined ? new Error(error) : error;
    clearConfigAttempt();
    throw error;
  };

  const apply = (_formSettings, originalSettings = {}, state) => {
    const buildCommand = () => composeApplyCommand(originalSettings, state);

    return checkNothingPending()
      .then(buildCommand)
      .then(submitCommand)
      .then(mayWarnUserIfHostnameChanged(originalSettings, state))
      .catch(notifyAndRevert);
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their answers would be those
  // of the whole cluster. The settings are asked again for the BQN next.
  return bqnId === undefined ? (
    <Loading />
  ) : (
    <SettingsView
      key={`general-settings-bqn${bqnId}`}
      load={() => load(mode, bqnId)}
      apply={apply}
      clear={clear}
      title="General Settings"
      applyActionClassName="hidden-to-operators"
      subtitle={
        <small>
          Press <code>Apply</code> button at the end of the page to set the new
          configuration values.
        </small>
      }
      mdHelp={help}
      returnView={returnView}
    >
      <GeneralSettings />
      <SettingsList />
    </SettingsView>
  );
};

export default ConfigGeneralSettings;
